import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
	BanIcon,
	Button, ButtonColor, CheckIcon, EditIcon, Input, InputSize, useClickOutside,
} from '@aleph/shared-components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

type IProps = {
	name: string;
	setName: (name: string) => void;
};

export const NameInput = ({ name, setName }: IProps) => {
	const { t } = useTranslation();

	const [nameEditing, setNameEditing] = useState(false);
	const [hovering, setHovering] = useState(false);
	const [value, setValue] = useState(name);

	const onCancel = useCallback(() => {
		setValue(name);
		setNameEditing(false);
		setHovering(false);
	}, [value, name]);

	const onConfirm = useCallback(() => {
		if (!value.length || !value.trim().length) {
			setValue(name);
			setNameEditing(false);
			setHovering(false);

			return;
		}
		setName(value);
		setNameEditing(false);
		setHovering(false);
	}, [value, name]);

	useEffect(() => {
		const onEnter = (e: KeyboardEvent) => {
			if (e.key === 'Enter') {
				onConfirm();
			}
		};

		document.addEventListener('keydown', onEnter);

		return () => {
			document.removeEventListener('keydown', onEnter);
		};
	}, [value, name]);

	const nameRef = useClickOutside(onCancel);

	return (
		<div
			className='flex flex-1 items-center gap-[8px] border-box overflow-hidden'
			ref={nameRef}
		>
			<div className="text-[#D9D9D9] text-[24px]">/</div>
			<div className='flex flex-1 h-[48px] overflow-hidden'>
				{!nameEditing ? (
					<div
						data-testid="edit"
						className={classNames('flex max-w-full items-center gap-[8px] text-[18px] text-gray-950 cursor-pointer', {
							'hover:text-blue': hovering,
						})}
						onClick={() => setNameEditing(prev => !prev)}
						onMouseOver={() => setHovering(true)}
						onMouseOut={() => setHovering(false)}
					>
						<span className='truncate'>
							{name}
						</span>
						{hovering && (
							<EditIcon
								width="16px"
								height="16px"
							/>
						)}
					</div>
				) : (
					<Input
						autoFocus
						defaultValue={name}
						value={value}
						size={InputSize.LARGE}
						onChange={e => setValue(e.target.value)}
						className="w-full max-w-[800px]"
						suffix={
							<div className="flex gap-[8px]">
								<Button
									leftNode={
										<BanIcon
											height="16px"
											width="16px"
										/>
									}
									appearance="ghost"
									size="medium"
									color={ButtonColor.RED}
									onClick={onCancel}
								/>

								<motion.div
									initial={{ width: 0 }}
									animate={{ width: 'auto' }}
									transition={{ duration: 0.1 }}
								>
									<Button
										title={t('sc: confirm', 'Confirm')}
										leftNode={<CheckIcon />}
										size="medium"
										onClick={onConfirm}
									/>
								</motion.div>

							</div>
						}
					/>
				)}
			</div>
		</div>
	);
};
